/**
 * Bake a brush's `scaleX`/`scaleY` MAGNITUDE into its geometry, so it carries no scale. Path-like vectors keep theirs.
 *
 * ⚠️ MAGNITUDE ONLY. The mirror is the scale's SIGN and is baked by `flipPathGeometry`, which also moves the origin; doing both double-corrects.
 *
 * ⚠️ `brush` is not a path — its outline is rebuilt from the scaled centerline, or the two stop describing the same stroke.
 */
import { VECTOR_TYPES } from '~/lib/flipObject'
import { localPivot, originOf, positionFromOrigin, rawPathBounds } from '~/lib/objectBounds'
import { buildOutlineFromCenterline, normalizeBrushOutline } from '~/lib/svg/brushUtils'
import { rawPathToString, resetPathOrigin, stringToRawPath, transformRawPath } from '~/lib/svg/paths'

/**
 * The position that keeps the baked object where the scaled one rendered: the old data origin, scaled about the pivot, plus the bake's re-normalization `offset` (new-data units). The pivot is a fraction of the extent, so for a pure path scale this returns `x`/`y` unchanged; a rebuilt brush outline need not scale exactly.
 */
const bakedPosition = (obj, sx, sy, offset, next) => {
	const p = localPivot(obj)
	const r = ((obj.rotation ?? 0) * Math.PI) / 180
	const cos = Math.cos(r),
		sin = Math.sin(r)
	const lx = offset.x - p.x * sx,
		ly = offset.y - p.y * sy
	const origin = { x: (obj.x ?? 0) + lx * cos - ly * sin, y: (obj.y ?? 0) + lx * sin + ly * cos }
	return positionFromOrigin(next, origin)
}

/** Below this the scale is 1 for every practical purpose, and baking it would rewrite geometry for nothing. Matches the resize handle's own `isScaling` epsilon. */
const EPSILON = 0.001

/** Does this object carry a scale its type is not allowed to have? */
export function hasResidualScale(obj) {
	if (!obj || !VECTOR_TYPES.has(obj.type)) return false
	const sx = Math.abs(obj.scaleX ?? 1)
	const sy = Math.abs(obj.scaleY ?? 1)
	return Math.abs(sx - 1) > EPSILON || Math.abs(sy - 1) > EPSILON
}

/**
 * Scale a brush's centerline and rebuild its outline from it.
 *
 * @returns {{data, centerline, rawPoints, offsetX, offsetY}} geometry plus the local origin shift the caller must fold into `x`/`y`.
 */
export function scaleBrushGeometry(obj, sx, sy) {
	const clRaw = stringToRawPath(obj.centerline)
	transformRawPath(clRaw, sx, 0, 0, sy, 0, 0)
	const clAbsSegment = clRaw[0]

	const strokeWidth = (obj.minWidth || 0) + (obj.widthGrowth ?? obj.maxWidth ?? 8)
	const rebuilt = buildOutlineFromCenterline(
		rawPathToString(clRaw),
		strokeWidth,
		obj.strokeCap || 'round',
		obj.smoothness ?? 50,
		obj.minWidth || 0,
		obj.widthProfile
	)

	let rawPoints = obj.rawPoints
	if (rawPoints) {
		const nums = rawPoints.split(',').map(Number)
		const ox = nums[0]
		const oy = nums[1]
		for (let i = 0; i < nums.length; i += 2) {
			nums[i] = (nums[i] - ox) * sx + ox
			nums[i + 1] = (nums[i + 1] - oy) * sy + oy
		}
		rawPoints = nums.join(',')
	}

	const outline = { data: rebuilt?.data ?? obj.data, x: rebuilt?.x ?? 0, y: rebuilt?.y ?? 0 }
	normalizeBrushOutline(outline)
	const offsetX = outline.x
	const offsetY = outline.y

	const centerline = clAbsSegment.slice()
	for (let i = 0; i < centerline.length; i++) centerline[i] -= i % 2 ? offsetY : offsetX

	return { data: outline.data, centerline: rawPathToString([centerline]), rawPoints, offsetX, offsetY }
}

/**
 * The patch that bakes `obj`'s residual scale into its geometry, or `null` when there is nothing to do — scale already 1, not a vector type, or no `data` to bake into.
 *
 * ⚠️ The patch CLEARS `scaleX`/`scaleY` to `undefined` rather than setting them to `1`. A vector type declares neither — the point of the bake is that the geometry carries the scale — so a stored `1` is a param the contract has no entry for, and no reader can tell it from a real value. Clearing rather than omitting is what keeps the bake idempotent under a spread: `{...obj, ...patch}` must not leave the original scale in place, or the second pass scales the geometry again. A caller writing to Redux should `delete` the keys outright.
 */
export function normalizeVectorScale(obj) {
	if (!hasResidualScale(obj) || !obj.data) return null

	const sx = Math.abs(obj.scaleX ?? 1)
	const sy = Math.abs(obj.scaleY ?? 1)

	if (obj.type === 'brush' && obj.centerline) {
		const geo = scaleBrushGeometry(obj, sx, sy)
		const bounds = rawPathBounds(stringToRawPath(geo.data))
		const patch = {
			id: obj.id,
			data: geo.data,
			centerline: geo.centerline,
			width: Math.max(0, bounds.x + bounds.width),
			height: Math.max(0, bounds.y + bounds.height),
			scaleX: undefined,
			scaleY: undefined,
		}
		Object.assign(patch, bakedPosition(obj, sx, sy, { x: geo.offsetX, y: geo.offsetY }, { ...obj, ...patch }))
		if (geo.rawPoints != null) {
			// rawPoints are anchored with their first point on the outline's data origin.
			const origin = originOf({ ...obj, ...patch })
			const nums = geo.rawPoints.split(',').map(Number)
			const dx = origin.x - nums[0]
			const dy = origin.y - nums[1]
			if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
				for (let i = 0; i < nums.length; i += 2) {
					nums[i] += dx
					nums[i + 1] += dy
				}
			}
			patch.rawPoints = nums.join(',')
		}
		return patch
	}

	// path / rect / circle, and a brush with no centerline.
	const rawPath = stringToRawPath(obj.data)
	transformRawPath(rawPath, sx, 0, 0, sy, 0, 0)
	const origin = resetPathOrigin(rawPath)
	const bounds = rawPathBounds(stringToRawPath(origin.data))

	const patch = {
		id: obj.id,
		data: origin.data,
		width: Math.max(0, bounds.x + bounds.width),
		height: Math.max(0, bounds.y + bounds.height),
		scaleX: undefined,
		scaleY: undefined,
	}
	return { ...patch, ...bakedPosition(obj, sx, sy, origin, { ...obj, ...patch }) }
}

export default normalizeVectorScale
